import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';
import {
  ensureExemptSubFileCategoryIdsLoaded,
  fileCategoryExemptFromSubFileCategory,
  normalizeFileCategoryLabel,
} from '../utils/subFileCategoryRules';
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export type CategoryAllocationExistsSupervisor = {
  userId: string;
  title: string;
  displayName: string;
  firstName: string;
  lastName: string;
};

export type CategoryAllocationExistsUser = {
  caseRequestCategoryAllocationUserId: string;
  userId: string;
  userDisplayName: string;
  userTitle: string;
};

export type CategoryAllocationExistsResult = {
  allocationExists: boolean;
  categoryAllocationId: string;
  fileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryId: string;
  subFileCategoryName: string;
  supervisor: CategoryAllocationExistsSupervisor | null;
  users: CategoryAllocationExistsUser[];
};

type ApiSupervisorRow = {
  user_id?: string;
  title?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
};

type ApiUserRow = {
  case_request_category_allocation_user_id?: string;
  user_id?: string;
  user_display_name?: string;
  user_title?: string;
};

type ApiCheckRow = {
  allocation_exists?: boolean;
  category_allocation_id?: string;
  file_category_id?: string;
  file_category_name?: string;
  sub_file_category_id?: string;
  sub_file_category_name?: string;
  supervisor?: ApiSupervisorRow | null;
  users?: ApiUserRow[];
};

function normalizeSupervisor(row: ApiSupervisorRow | null | undefined): CategoryAllocationExistsSupervisor | null {
  if (!row || typeof row !== 'object') return null;
  const userId = str(row.user_id);
  if (!userId) return null;
  return {
    userId,
    title: str(row.title),
    displayName: str(row.display_name),
    firstName: str(row.first_name),
    lastName: str(row.last_name),
  };
}

function normalizeUsers(data: unknown): CategoryAllocationExistsUser[] {
  if (!Array.isArray(data)) return [];
  const out: CategoryAllocationExistsUser[] = [];
  for (const item of data) {
    const r = (item && typeof item === 'object' ? (item as ApiUserRow) : {}) as ApiUserRow;
    const caseRequestCategoryAllocationUserId = str(r.case_request_category_allocation_user_id);
    const userId = str(r.user_id);
    if (!caseRequestCategoryAllocationUserId || !userId) continue;
    out.push({
      caseRequestCategoryAllocationUserId,
      userId,
      userDisplayName: str(r.user_display_name),
      userTitle: str(r.user_title),
    });
  }
  return out;
}

function normalizeCheckResult(data: unknown): CategoryAllocationExistsResult {
  const r = (data && typeof data === 'object' ? (data as ApiCheckRow) : {}) as ApiCheckRow;
  return {
    allocationExists: r.allocation_exists === true,
    categoryAllocationId: str(r.category_allocation_id),
    fileCategoryId: str(r.file_category_id),
    fileCategoryName: str(r.file_category_name),
    subFileCategoryId: str(r.sub_file_category_id),
    subFileCategoryName: str(r.sub_file_category_name),
    supervisor: normalizeSupervisor(r.supervisor),
    users: normalizeUsers(r.users),
  };
}

/** CMS-CCA-12 — Check whether a category allocation exists for file + sub file category. */
export async function checkCategoryAllocationExists(input: {
  fileCategoryId: string;
  subFileCategoryId?: string;
  fileCategoryName?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CategoryAllocationExistsResult>> {
  await ensureExemptSubFileCategoryIdsLoaded();

  const fileCategoryId = input.fileCategoryId.trim();
  const subFileCategoryId = (input.subFileCategoryId ?? '').trim();
  const fileCategoryName = normalizeFileCategoryLabel(input.fileCategoryName);
  const emptyResult: CategoryAllocationExistsResult = {
    allocationExists: false,
    categoryAllocationId: '',
    fileCategoryId: '',
    fileCategoryName: '',
    subFileCategoryId: '',
    subFileCategoryName: '',
    supervisor: null,
    users: [],
  };

  if (!fileCategoryId) {
    return { success: true, data: emptyResult };
  }

  const exemptFromSubCategory = fileCategoryExemptFromSubFileCategory({
    id: fileCategoryId,
    name: fileCategoryName,
  });
  if (!subFileCategoryId && !exemptFromSubCategory) {
    return { success: true, data: emptyResult };
  }

  const qs = new URLSearchParams({ fileCategoryId });
  if (subFileCategoryId) qs.set('subFileCategoryId', subFileCategoryId);
  const res = await caseApiRequest<unknown>(`/category-allocations/check?${qs.toString()}`, {
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeCheckResult(res.data) };
}
